/**
 * Elements that draw after they are inserted. Seranova's tag renders its label
 * into a shadow root a frame or two after it connects, so until then it is 0px
 * wide. Responsive measures every fit on a fresh copy of the table, where these
 * elements have not drawn yet, so it under-sizes their columns and keeps a
 * column too many on screen.
 */
export const LATE_SIZED_SELECTOR = "sera-tag";

const nextFrame = () =>
  new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

/**
 * The narrowest each cell can render once its late elements have drawn,
 * measured on copies in a shrink-to-fit table placed beside the real one, with
 * its classes, so the same cell padding and fonts apply. Cells without a late
 * element are skipped: Responsive already measures those correctly.
 */
export async function measureLateSizedCells(
  cells: HTMLElement[],
  realTable: HTMLTableElement
): Promise<number[]> {
  const late = cells.filter((cell) => cell.querySelector(LATE_SIZED_SELECTOR));
  if (!late.length) return [];

  const host = document.createElement("div");
  host.style.cssText =
    "position:absolute;top:0;left:-10000px;visibility:hidden;height:0;overflow:hidden";
  const table = document.createElement("table");
  // Without Responsive's own classes: `collapsed` pads every row's first cell
  // for the tap slot, and each copy is its row's first cell.
  table.className = realTable.className
    .split(/\s+/)
    .filter((name) => name !== "collapsed" && !name.startsWith("dtr-"))
    .join(" ");
  table.style.width = "1px";
  const tbody = table.createTBody();
  const copies = late.map((cell) => {
    const copy = cell.cloneNode(true) as HTMLElement;
    copy.classList.remove("dtr-hidden");
    copy.style.display = "";
    tbody.insertRow().appendChild(copy);
    return copy;
  });
  host.appendChild(table);
  (realTable.parentElement ?? document.body).appendChild(host);
  try {
    if (window.customElements?.get(LATE_SIZED_SELECTOR) === undefined) {
      await Promise.race([
        window.customElements?.whenDefined(LATE_SIZED_SELECTOR),
        new Promise((resolve) => setTimeout(resolve, 1000))
      ]);
    }
    await nextFrame();
    await nextFrame();
    return copies.map((copy) => copy.offsetWidth);
  } finally {
    host.remove();
  }
}

/**
 * Raise each column's measured width to its floor, never lower it. Returns
 * whether anything changed, so a caller knows when a re-fit is worth asking for.
 */
export function applyWidthFloors(
  columns: Array<{ minWidth: number }>,
  floors: Map<number, number>
): boolean {
  let raised = false;
  floors.forEach((floor, index) => {
    const column = columns[index];
    if (column && floor > column.minWidth) {
      column.minWidth = floor;
      raised = true;
    }
  });
  return raised;
}
